import {FeatureAppDefinition, FeatureServices, Logger} from '@feature-hub/core';
import {PageInfoServiceV1} from '@volkswagen-onehub/page-info-service';
import {TrackingManagerV1} from '@volkswagen-onehub/tracking-service';
import React from 'react';
import dependencies from './dependencies.json';
import {FaContentServiceV1} from '@volkswagen-onehub/fa-content-service';
import {FeatureServicesContext} from './use-feature-services';
import {ServiceConfigProviderV1} from '@volkswagen-onehub/service-config-provider';
import {ApiKeyProviderV1} from '@volkswagen-onehub/api-key-provider';
import {LayerManagerV27} from '@volkswagen-onehub/layer-manager';
import {
  LocaleServiceConfig,
  LocaleServiceV1,
} from '@volkswagen-onehub/locale-service';
import {CtdUserInteractionContent} from '@volkswagen-onehub/ctd-user-interaction';
import {ShowroomNavigationManagerV11} from '@volkswagen-onehub/showroom-navigation';
import {ServiceConfigEndpointProvider} from '@volkswagen-onehub/service-config-endpoint-provider';
import {App, StyledMain} from './components/app';
import {ErrorPage} from './components/error';
import {createBffUrl, loadBff} from './loadBff';
import {
  BffResponse,
  ModelFilter,
} from '@volkswagen-onehub/car-technical-details-bff';
import {DisclaimerManagerV1} from '@volkswagen-onehub/disclaimer-manager';
import {
  ContentLayerV2Content,
  ThemeProvider,
} from '@volkswagen-onehub/components-core';
import {
  InspectFaServiceV1,
  MockSettings,
} from '@volkswagen-onehub/inspect-fa-service';
import {inspectDataHolder} from './utils/inspector/inspector';
import {createCloseTopLayerFunction} from './components/create-close-top-layer-fn';
import {ServerRequestV1} from '@feature-hub/server-request';
import {AsyncSsrManagerV1} from '@feature-hub/async-ssr-manager';
import {SerializedStateManagerV1} from '@feature-hub/serialized-state-manager';
import {FeatureAppMemoryStoreV1} from '@volkswagen-onehub/feature-app-memory-store';
import {
  CommonFeatureAppConfig,
  UniversalState,
  manageBffSerializationAndCaching,
  saveBffResponse,
  NoCacheMemoryStore,
  MocksServerName,
} from '@volkswagen-onehub/vw-starter-feature-app-support';

export interface TechnicalDetailsFeatureAppDependencies
  extends FeatureServices {
  readonly 's2:logger'?: Logger;
  readonly 'fa-content-service': FaContentServiceV1;
  readonly 'service-config-provider': ServiceConfigProviderV1;
  readonly 'disclaimer-manager': DisclaimerManagerV1;
  readonly 'api-key-provider': ApiKeyProviderV1;
  readonly 'layer-manager': LayerManagerV27;
  readonly 'locale-service': LocaleServiceV1;
  readonly 'page-info-service': PageInfoServiceV1;
  readonly 'service-config-endpoint-provider': ServiceConfigEndpointProvider;
  readonly 'showroom-navigation': ShowroomNavigationManagerV11;
  readonly 'tracking': TrackingManagerV1;
  readonly 'inspect-fa-service'?: InspectFaServiceV1;
  readonly 's2:serialized-state-manager': SerializedStateManagerV1; // TODO necessary for SSR support. Remove otherwise.
  readonly 's2:server-request': ServerRequestV1; // TODO necessary for SSR support. Remove otherwise.
  readonly 's2:async-ssr-manager': AsyncSsrManagerV1; // TODO necessary for SSR support. Remove otherwise.
  readonly 'feature-app-memory-store': FeatureAppMemoryStoreV1<
    UniversalState<BffResponse>
  >;
}

interface RenderWithLoadingPromise {
  loadingPromise?: Promise<BffResponse>;
  render(): React.ReactNode;
}

export type FaContent = CtdUserInteractionContent;

export type ViewMode = 'layer' | 'embedded';

const errorFaDefinition = (
  message: string,
  viewMode: ViewMode,
  layerManager: LayerManagerV27,
  pageInfoService: PageInfoServiceV1
) => {
  const closeLayer = createCloseTopLayerFunction(layerManager, pageInfoService);
  return {
    render() {
      return viewMode === 'layer' ? (
        <ContentLayerV2Content
          closeButtonLabel=""
          contentHeadline=""
          onClose={closeLayer}
        >
          <StyledMain isEmbedded={false}>
            <ErrorPage message={message} />
          </StyledMain>
        </ContentLayerV2Content>
      ) : (
        <div></div>
      );
    },
  };
};

export const featureAppDefinition: FeatureAppDefinition<
  RenderWithLoadingPromise,
  TechnicalDetailsFeatureAppDependencies,
  CommonFeatureAppConfig
> = {
  ...dependencies,
  create: ({featureServices, config, baseUrl}) => {
    const {
      's2:logger': logger = console,
      'api-key-provider': apiKeyProvider,
      'locale-service': localeService,
      's2:serialized-state-manager': serializedStateManager, // TODO necessary for SSR support. Remove otherwise.
      's2:server-request': serverRequest, // TODO necessary for SSR support. Remove otherwise.
      's2:async-ssr-manager': asyncSsrManager, // TODO necessary for SSR support. Remove otherwise.
      'feature-app-memory-store': fsMemoryStore,
      'service-config-endpoint-provider': serviceConfigEndpointProvider,
      'showroom-navigation': showroomNavigationManager,
      'fa-content-service': contentService,
      'page-info-service': pageInfoService,
      'inspect-fa-service': inspectFaService,
      'layer-manager': layerManager,
    } = featureServices;

    // in the client state shouldn't be shared between fa instances
    const memoryStore = serverRequest
      ? fsMemoryStore
      : new NoCacheMemoryStore<UniversalState<BffResponse>>();

    // retrieve serialized state on client and store it on server
    manageBffSerializationAndCaching(
      serializedStateManager,
      memoryStore,
      logger
    );

    const content = contentService.content as FaContent;
    const viewMode = content.viewMode || 'layer';

    if (typeof baseUrl === 'undefined') {
      logger.warn('ctd: no base url defined');
      return errorFaDefinition(
        'technical error',
        viewMode,
        layerManager,
        pageInfoService
      );
    }

    const apiKey = apiKeyProvider.apiKey;
    if (!apiKey) {
      logger.warn('ctd: no api key');
      return errorFaDefinition(
        'technical error',
        viewMode,
        layerManager,
        pageInfoService
      );
    }

    const {mockIds, mocksServerName} = getMockConfig(config, inspectFaService);

    if (!content) {
      logger.warn('ctd: no models defined');
      return errorFaDefinition(
        'no models defined',
        viewMode,
        layerManager,
        pageInfoService
      );
    }

    const tenant = content.tenant || 'private';

    const models: ModelFilter[] | undefined =
      content.models && content.models.length > 0
        ? content.models.map((m) => ({...m, tenant}))
        : undefined;

    const showroomNodeId =
      content.showroomNodeId ||
      (!models ? showroomNavigationManager.showroomNodeId : undefined);

    inspectDataHolder.showroomNodeIdFromService =
      showroomNavigationManager.showroomNodeId;
    inspectDataHolder.showroomNodeIdFromContent = content.showroomNodeId;
    inspectDataHolder.modelsFromContent = models;

    const debug = serverRequest
      ? new URL(serverRequest.url).searchParams.get('debug') || undefined
      : new URL(window.location.href).searchParams.get('debug') || undefined;

    const {language, countryCode, cacheBustingAllowed, endpoint} = getBffParams(
      localeService,
      serviceConfigEndpointProvider,
      inspectFaService?.mockSettings
    );

    const dimensionImagesTrim = content.dimensionImagesTrim;
    const defaultTrim = content.defaultTrim;
    const profileId = content.profileId;

    const bffUrl = createBffUrl(baseUrl, {
      language,
      countryCode,
      endpoint,
      showroomNodeId,
      models,
      oneapiKey: apiKey,
      cacheBustingAllowed,
      mockIds,
      mocksServerName,
      debug,
      dimensionImagesTrim,
      defaultTrim,
      profileId,
    });

    const loadStatus = memoryStore.getState()?.status || 'loading';

    const bffLoadComplete = loadStatus === 'loaded' || loadStatus === 'error';

    const bffResponse = bffLoadComplete
      ? undefined
      : loadBff(bffUrl).then((response) => {
          if (inspectFaService && response.cacheMonitoring)
            inspectFaService.addCacheMonitoring({
              name: 'bff',
              url: bffUrl,
              ssr: false,
              type: 'BFF',
              ...response.cacheMonitoring,
            });
          if (response) {
            logger.debug('save bff response');
            inspectDataHolder.undisplayableGroup = response.undisplayableGroup;
            saveBffResponse(memoryStore, response);
            return response;
          }

          throw 'failure';
        });

    if (serverRequest && bffResponse) {
      asyncSsrManager?.scheduleRerender(bffResponse);
    }

    return {
      loadingPromise:
        serverRequest || bffLoadComplete ? undefined : bffResponse,
      render() {
        const state = memoryStore.getState();

        if (state?.status === 'error') {
          return <ErrorPage message={'unknown error'} />;
        }
        if (!state) {
          // TODO display spinner
          return null;
        }
        if (state.status == 'loading') {
          return <ErrorPage message={'state should not be loading'} />;
        }
        return (
          <FeatureServicesContext.Provider value={featureServices}>
            <ThemeProvider theme="main">
              <App
                data={state?.payload}
                viewMode={viewMode}
                layerManager={layerManager}
                withoutHeader={content.layerWithoutHeader}
                bffUrl={bffUrl}
              />
            </ThemeProvider>
          </FeatureServicesContext.Provider>
        );
      },
    };
  },
};

const getMockConfig = (
  config?: CommonFeatureAppConfig,
  inspectFaService?: InspectFaServiceV1
): {mockIds?: string[]; mocksServerName?: MocksServerName} => {
  if (
    inspectFaService &&
    inspectFaService.mocksServerConfig &&
    inspectFaService.mockSettings &&
    inspectFaService.mockSettings.mockIds &&
    inspectFaService.mockSettings.mockIds.length > 0
  ) {
    return {
      mockIds: inspectFaService.mockSettings.mockIds,
      mocksServerName: 'global',
    };
  }
  if (typeof config === 'object') {
    const {mockIds, mocksServerName} = config;
    return {
      mockIds: mockIds as string[],
      mocksServerName: mocksServerName == 'local' ? 'local' : 'global',
    };
  }
  return {
    mockIds: [],
    mocksServerName: undefined,
  };
};

export default featureAppDefinition;
function getBffParams(
  localeService: LocaleServiceConfig,
  serviceConfigEndpointProvider: ServiceConfigEndpointProvider,
  mockSettings: MockSettings | undefined
): {
  language: string;
  countryCode: string;
  endpoint: string;
  cacheBustingAllowed: boolean;
} {
  if (mockSettings && mockSettings.environment) {
    return {
      language: mockSettings.environment.language,
      countryCode: mockSettings.environment.countryCode,
      cacheBustingAllowed: mockSettings.environment.cacheBustingAllowed,
      endpoint: mockSettings.environment.mockEndpoint,
    };
  }
  return {
    language: localeService.language,
    countryCode: localeService.countryCode,
    cacheBustingAllowed: serviceConfigEndpointProvider.cacheBustingAllowed,
    endpoint: serviceConfigEndpointProvider.endpoint,
  };
}
